import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DashboardBottom from "./DashboardBottom";
import DashboardNews from "./DashboardNews";
import DashboardTop from "./DashboardTop";
import Pulse from "../Loader/Pulse";
import "../../css/Dashboard/Dashboard.css";
import { GetCookie } from "../../exports/CookieManagement";
import { titleCase } from "../../exports/UtilFunctions";
import { GetRoleType } from "../../exports/RoleTypes";
import ServerRequest from "../../exports/ServerRequest";
import useAccount from "../../hooks/useAccount";

const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;

function Dashboard() {
  const { account } = useAccount();
  const [stockData, setStockData] = useState(null);
  const [mfData, setmfData] = useState(null)
  const { account_code } = account;
  const account_token = GetCookie("token");
  const role_type = GetRoleType(GetCookie("role")) || "CUSTOMER";

  async function GetClientInfo() {
    const role = role_type === "CUSTOMER" ? "customer" : "superuser";
    const response = await fetch(
      `${REQUEST_BASE_URL}/clientinfo/${role}?token=${account_token}`,
    );
    const data = await response.json();
    if (data.error)
      throw new Error(data.message || "Failed to fetch client info");
    return data;
  }

  async function GetPortfolioSummary() {
    const response = await fetch(
      `${REQUEST_BASE_URL}/mf/portfolio?token=${account_token}`,
    );
    const portfolioData = await response.json();
    if (portfolioData.error)
      throw new Error("Failed to fetch portfolio summary");
    return portfolioData;
  }

  async function GetPortfolio(account_code) {
    const response = await ServerRequest({
      method: "POST",
      url: `/portfolio?account_code=${account_code}`,
      data: {
        portfolioName: "",
      },
    });
    const data = response;
    return data;
  }

  const fetchTransactionWithNAV = async () => {
    if (!account_token && !account_code) return;

    try {
      const url = `/mf/portfolio?token=${account_token}`; // Customer

      const response = await ServerRequest({ url, method: "get" });

      const portfolioData = response?.data || [];

      // Separate rows
      const rowsWithISIN = portfolioData.filter((item) => item.isin);
      const footerRow = portfolioData.find((item) => !item.isin);

      // Enrich with NAV change
      const enrichedRows = await Promise.all(
        rowsWithISIN.map(async (item) => {
          try {
            const navResp = await ServerRequest({
              url: `/mf/latestnav/${item.isin}`,
              method: "get",
            });
            return { ...item, navChange: navResp?.result?.navChange ?? null };
          } catch {
            return { ...item, navChange: null };
          }
        }),
      );

      // Append footer row if it exists
      const finalData = footerRow ? [...enrichedRows, footerRow] : enrichedRows;
      const transactionData = finalData.filter(
        (p) => p.investment_value !== "0.00",
      );

      setmfData(transactionData);
    } catch (err) {
      console.error("Error fetching portfolio/NAV data:", err);
      setmfData([]); // fallback
    }
  };

  useEffect(() => {
    GetPortfolio(account_code)
      .then((portfolio) => {
        let { portfolioData } = portfolio;
        setStockData(portfolioData);
      })
      .catch(() => {
        setStockData([]);
      });
      fetchTransactionWithNAV();
  }, [account]);

  const {
    data: clientData,
    isLoading: clientLoading,
    isError: clientError,
  } = useQuery(["client-info", account_token], GetClientInfo, {
    enabled: !!account_token,
  });

  const {
    data: portfolioData,
    isLoading: portfolioLoading,
    isError: portfolioError,
  } = useQuery(["portfolio-summary", account_token], GetPortfolioSummary, {
    enabled: !!account_token,
  });

  const accountName = titleCase(clientData?.account_data?.name || "User");

  if (clientLoading || portfolioLoading) {
    return (
      <div className="swift-dashboard">
        <div className="dashboard-loading">
          <Pulse />
{/* <p>Loading dashboard, please wait...</p> */}
        </div>
      </div>
    );
  }

  if (clientError || portfolioError) {
    return (
      <div className="swift-dashboard">
        <div className="dashboard-loading">
          <p>Failed to load dashboard. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="swift-dashboard">
      <div className="dashboard-client">
        <p className="welcome-message">
          Welcome back <span className="text-primary">{accountName}</span>
        </p>
      </div>

      {clientData && (
        <div className="dashboard-content">
          {role_type === "CUSTOMER" ? (
            <>
              <div className="dashboard-left">
                <DashboardTop
                  user_role_type={role_type}
                  data={clientData}
                  portfolioData={portfolioData}
                  stockData={stockData}
                  mfData={mfData}
                />
              </div>
              <DashboardNews />
            </>
          ) : (
            <>
              <div className="dashboard-left">
                <DashboardNews />
                <DashboardTop user_role_type={role_type} data={clientData} />
              </div>
              <DashboardBottom />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
